import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { assetNavigationHref } from '../utils/assetOpener';
import { Paperclip, Link2, X, FileText, Image as ImageIcon, Video, ExternalLink, Loader2, Pencil, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcon = (type) => {
  if (type === 'image') return ImageIcon;
  if (type === 'video') return Video;
  if (type === 'link') return ExternalLink;
  return FileText;
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return '';
  }
};


/**
 * Reusable Resources editor (Phase 86).
 *
 * A Resource may be a File OR an external Link. Each resource carries:
 *   { type: 'file'|'image'|'video'|'link',
 *     url: str,
 *     name: str,               // optional display title
 *     size?: int,              // files only
 *     content_type?: str,      // files only
 *     uploaded_by?: str,       // user id
 *     uploaded_by_name?: str,
 *     uploaded_at?: str (ISO)  }
 *
 * Props:
 *   value       — Resource[] (never mutates the array)
 *   onChange    — (Resource[]) => void
 *   label       — heading; defaults to "Resources"
 *   compact     — smaller inline layout
 *   data-testid — root test id
 */
const ResourcesField = ({
  value = [],
  onChange,
  label = 'Resources',
  compact = false,
  'data-testid': testId = 'resources-field',
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const next = [...value];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/attachments/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        next.push(r.data);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    onChange(next);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddLink = () => {
    if (!linkUrl.trim()) {
      toast.error('URL is required');
      return;
    }
    const url = linkUrl.trim();
    const name = linkName.trim() || url;
    onChange([...value, {
      type: 'link',
      url,
      name,
      // The backend never sees link resources — stamp locally so the audit
      // trail is still populated for future modules using this component.
      uploaded_at: new Date().toISOString(),
    }]);
    setLinkUrl('');
    setLinkName('');
    setLinkMode(false);
  };

  const handleRemove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditTitle(value[idx]?.name || '');
  };

  const saveEdit = () => {
    if (editingIdx == null) return;
    const next = value.map((r, i) => (
      i === editingIdx ? { ...r, name: editTitle.trim() || r.url } : r
    ));
    onChange(next);
    setEditingIdx(null);
    setEditTitle('');
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditTitle('');
  };

  return (
    <div data-testid={testId}>
      {label && <Label className="block mb-1.5">{label}</Label>}

      <div className={`flex gap-2 ${compact ? 'mb-2' : 'mb-3'}`}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilePick}
          data-testid={`${testId}-file-input`}
        />
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-8 text-xs"
          data-testid={`${testId}-upload-btn`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Paperclip className="w-3.5 h-3.5 mr-1" />}
          Add File
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => setLinkMode(v => !v)}
          className="h-8 text-xs"
          data-testid={`${testId}-link-btn`}
        >
          <Link2 className="w-3.5 h-3.5 mr-1" /> Add Link
        </Button>
      </div>

      {linkMode && (
        <div className="flex gap-2 mb-3 p-2 border rounded-lg bg-gray-50 dark:bg-gray-800" data-testid={`${testId}-link-form`}>
          <Input
            className="flex-1 h-8 text-xs"
            placeholder="https://..."
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            data-testid={`${testId}-link-url`}
          />
          <Input
            className="w-40 h-8 text-xs"
            placeholder="Title (optional)"
            value={linkName}
            onChange={e => setLinkName(e.target.value)}
            data-testid={`${testId}-link-name`}
          />
          <Button type="button" size="sm" className="h-8 text-xs bg-pfap-green hover:bg-pfap-green/90" onClick={handleAddLink} data-testid={`${testId}-link-save`}>
            Add
          </Button>
        </div>
      )}

      {value.length > 0 && (
        <div className="space-y-1.5" data-testid={`${testId}-list`}>
          {value.map((att, i) => {
            const Icon = typeIcon(att.type);
            const href = assetNavigationHref(att.url, att.name);
            const isEditing = editingIdx === i;
            return (
              <div
                key={i}
                className="flex items-center gap-2 p-2 border rounded-lg bg-white dark:bg-gray-900"
                data-testid={`${testId}-item-${i}`}
              >
                <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                {isEditing ? (
                  <>
                    <Input
                      className="flex-1 h-7 text-xs"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                      data-testid={`${testId}-edit-input-${i}`}
                    />
                    <button
                      type="button"
                      onClick={saveEdit}
                      className="text-emerald-600 hover:text-emerald-700"
                      data-testid={`${testId}-edit-save-${i}`}
                      aria-label="Save title"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="text-gray-400 hover:text-gray-600"
                      data-testid={`${testId}-edit-cancel-${i}`}
                      aria-label="Cancel edit"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 min-w-0 text-xs text-gray-700 dark:text-gray-300 hover:text-emerald-600 truncate"
                      data-testid={`${testId}-link-${i}`}
                      title={att.name || att.url}
                    >
                      {att.name || att.url}
                    </a>
                    {(att.uploaded_by_name || att.uploaded_at) && (
                      <span className="text-[10px] text-gray-400 hidden sm:inline whitespace-nowrap" data-testid={`${testId}-meta-${i}`}>
                        {att.uploaded_by_name || ''}
                        {att.uploaded_by_name && att.uploaded_at ? ' · ' : ''}
                        {formatWhen(att.uploaded_at)}
                      </span>
                    )}
                    {att.size > 0 && (
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatBytes(att.size)}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => startEdit(i)}
                      className="text-gray-400 hover:text-gray-700 transition-colors"
                      data-testid={`${testId}-edit-${i}`}
                      aria-label="Edit title"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(i)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`${testId}-remove-${i}`}
                      aria-label="Remove resource"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Read-only Resources viewer — for detail dialogs.
 */
export const ResourcesView = ({ attachments = [], label = 'Resources', 'data-testid': testId = 'resources-view' }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div data-testid={testId}>
      <p className="text-xs font-medium text-gray-500 mb-2">{label} ({attachments.length})</p>
      <div className="space-y-1.5">
        {attachments.map((att, i) => {
          const Icon = typeIcon(att.type);
          return (
            <a
              key={i}
              href={assetNavigationHref(att.url, att.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 border rounded-lg bg-white dark:bg-gray-900 hover:border-emerald-400 transition-colors"
              data-testid={`${testId}-item-${i}`}
            >
              <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 text-xs text-gray-700 dark:text-gray-300 truncate" title={att.name || att.url}>{att.name || att.url}</span>
              {(att.uploaded_by_name || att.uploaded_at) && (
                <span className="text-[10px] text-gray-400 hidden sm:inline whitespace-nowrap">
                  {att.uploaded_by_name || ''}
                  {att.uploaded_by_name && att.uploaded_at ? ' · ' : ''}
                  {formatWhen(att.uploaded_at)}
                </span>
              )}
              {att.size > 0 && <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatBytes(att.size)}</span>}
              <ExternalLink className="w-3 h-3 text-gray-400" />
            </a>
          );
        })}
      </div>
    </div>
  );
};

// Backwards-compat aliases: some modules still import
// { AttachmentsField, AttachmentsView }. These aliases let us rename the
// filename to ResourcesField without a big-bang refactor.
export const AttachmentsField = ResourcesField;
export const AttachmentsView = ResourcesView;

export default ResourcesField;
